/**
 * Bloc « Position » de la fiche camion : où est le camion, à quand remonte le
 * dernier signal, d'où vient la position. Les règles (signal, arrêt, source)
 * sont celles de la carte de flotte (`components/map/fleetRules.js`).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Marker } from 'react-leaflet';
import { MapPin } from 'lucide-react';

import MapSurface from '../map/MapSurface';
import SignalBadge from '../map/SignalBadge';
import CopierPosition from '../map/CopierPosition';
import { truckIcon } from '../map/icons';
import { lireFondPrefere } from '../map/fondPrefere';
import { couleurFamille } from '../map/status';
import {
  ageTexte,
  arretDepuisSecondes,
  aUnePosition,
  dateExacteTexte,
  dureeArretTexte,
  estEnMouvement,
  estPositionFraiche,
  libelleSource,
  vitesseTexte,
} from '../map/fleetRules';
import { trackingAPI } from '../../lib/api';
import useMaintenant from '../../hooks/useMaintenant';

const LIGNE = 'flex justify-between gap-3';

export default function TruckPositionCard({ truck }) {
  const maintenant = useMaintenant();
  const [etat, setEtat] = useState({ chargement: true, erreur: false, position: null });

  const charger = useCallback(() => {
    setEtat((e) => ({ ...e, chargement: true, erreur: false }));
    // Une erreur levée avant la requête finit aussi en « Réessayer ».
    Promise.resolve()
      .then(() => trackingAPI.getTruckLastPosition(truck.id))
      .then((position) => setEtat({ chargement: false, erreur: false, position }))
      .catch(() => setEtat({ chargement: false, erreur: true, position: null }));
  }, [truck.id]);

  useEffect(() => {
    charger();
  }, [charger]);

  const position = etat.position;
  const point = useMemo(
    () => (aUnePosition(position) ? [Number(position.latitude), Number(position.longitude)] : null),
    [position],
  );

  let contenu;
  if (etat.chargement && !position) {
    contenu = <p className="text-sm text-muted-foreground">Chargement de la position…</p>;
  } else if (etat.erreur) {
    contenu = (
      <p className="text-sm text-muted-foreground">
        Position indisponible pour le moment.{' '}
        <button type="button" onClick={charger} className="font-medium text-info underline-offset-2 hover:underline">
          Réessayer
        </button>
      </p>
    );
  } else if (!point) {
    contenu = <p className="text-sm text-muted-foreground">Aucune position reçue pour ce camion.</p>;
  } else {
    const signal = estPositionFraiche(position, maintenant);
    const arret = dureeArretTexte(arretDepuisSecondes(position, maintenant));
    const source = libelleSource(position.source);
    contenu = (
      <>
        <div className="h-48 overflow-hidden rounded-lg border border-border" data-testid="truck-position-map">
          <MapSurface
            layer={lireFondPrefere() || 'streets'}
            center={point}
            zoom={13}
            fitEnabled={false}
            scrollWheelZoom={false}
            mapClassName="h-full w-full"
          >
            <Marker
              position={point}
              icon={truckIcon({
                color: couleurFamille(position.trip_id ? 'route' : 'available'),
                matricule: truck.matricule,
                online: signal,
                heading: position.heading,
                moving: estEnMouvement(position, maintenant),
              })}
            />
          </MapSurface>
        </div>
        <dl className="space-y-1 text-sm">
          <div className={LIGNE}>
            <dt className="text-muted-foreground">Dernier signal</dt>
            <dd className="font-medium" title={dateExacteTexte(position) || undefined}>
              {ageTexte(position, maintenant) || 'Date inconnue'}
            </dd>
          </div>
          <div className={LIGNE}>
            <dt className="text-muted-foreground">Vitesse</dt>
            <dd className="font-medium">{vitesseTexte(position)}</dd>
          </div>
          {arret && (
            <div className={LIGNE}>
              <dt className="text-muted-foreground">À l’arrêt depuis</dt>
              <dd className="font-medium">{arret}</dd>
            </div>
          )}
          {source && (
            <div className={LIGNE}>
              <dt className="text-muted-foreground">Source</dt>
              <dd className="font-medium">{source}</dd>
            </div>
          )}
        </dl>
        <div className="grid grid-cols-2 gap-2">
          <Link
            to={`/tracking?camion=${encodeURIComponent(truck.id)}`}
            className="inline-flex items-center justify-center gap-1.5 rounded border border-border bg-card px-2 py-1 text-sm hover:bg-muted"
          >
            <MapPin className="h-4 w-4" aria-hidden="true" />
            Voir sur la carte
          </Link>
          <CopierPosition vehicule={position} />
        </div>
      </>
    );
  }

  const signalVisible = point && !etat.erreur ? estPositionFraiche(position, maintenant) : null;

  return (
    <section className="space-y-3 rounded-xl border border-border bg-card p-4" data-testid="truck-position">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base">Position</h2>
        {signalVisible !== null && <SignalBadge actif={signalVisible} />}
      </div>
      {contenu}
    </section>
  );
}
